import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { api, extractError } from "../lib/api";
import { useAuth, isAdmin } from "../context/AuthContext";
import { useLookups } from "../context/LookupOptionsContext";
import { PageHeader, SectionCard } from "../components/Ui";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { PADDY_LAB_METRICS } from "../lib/paddyLabConfig";

const COMPANY_QUALITY = "__COMPANY_VALIDATION__";

function emptyLimits() {
  return Object.fromEntries(PADDY_LAB_METRICS.map((m) => [m.key, { min: "", max: "" }]));
}

function mergeLimits(raw) {
  const base = emptyLimits();
  if (!raw) return base;
  for (const m of PADDY_LAB_METRICS) {
    const entry = raw[m.key] || {};
    base[m.key] = {
      min: entry.min ?? "",
      max: entry.max ?? "",
    };
  }
  return base;
}

function payloadLimits(formLimits) {
  const out = {};
  for (const m of PADDY_LAB_METRICS) {
    const row = formLimits[m.key] || {};
    const min = row.min === "" || row.min == null ? null : Number(row.min);
    const max = row.max === "" || row.max == null ? null : Number(row.max);
    if (min != null || max != null) {
      out[m.key] = { min, max };
    }
  }
  return out;
}

function LimitsTable({ limits, setLimit, testIdPrefix }) {
  return (
    <div className="overflow-x-auto rounded-md border">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b bg-muted/40 text-left">
            <th className="p-2 font-medium">Parameter</th>
            <th className="p-2 font-medium w-28">Min</th>
            <th className="p-2 font-medium w-28">Max</th>
          </tr>
        </thead>
        <tbody>
          {PADDY_LAB_METRICS.map((m) => (
            <tr key={m.key} className="border-b last:border-0">
              <td className="p-2 text-muted-foreground">
                {m.label}
                <span className="text-[10px] ml-1">{m.suffix}</span>
              </td>
              <td className="p-2">
                <Input
                  type="number"
                  step="0.01"
                  className="h-8"
                  value={limits[m.key]?.min ?? ""}
                  onChange={(e) => setLimit(m.key, "min", e.target.value)}
                  data-testid={`${testIdPrefix}-min-${m.key}`}
                />
              </td>
              <td className="p-2">
                <Input
                  type="number"
                  step="0.01"
                  className="h-8"
                  value={limits[m.key]?.max ?? ""}
                  onChange={(e) => setLimit(m.key, "max", e.target.value)}
                  data-testid={`${testIdPrefix}-max-${m.key}`}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function PaddyQualityLabProfilesPage() {
  const { user } = useAuth();
  const { optionsFor, lookupMap } = useLookups();
  const qualities = useMemo(
    () => optionsFor("paddy_variety").filter((q) => String(q.value).toUpperCase() !== COMPANY_QUALITY),
    [optionsFor, lookupMap],
  );

  const [profiles, setProfiles] = useState([]);
  const [companyLimits, setCompanyLimits] = useState(() => emptyLimits());
  const [quality, setQuality] = useState("");
  const [label, setLabel] = useState("");
  const [limits, setLimits] = useState(() => emptyLimits());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingCompany, setSavingCompany] = useState(false);
  const [rescanning, setRescanning] = useState(false);

  const canManage = isAdmin(user);

  const load = useCallback(async () => {
    if (!canManage) return;
    setLoading(true);
    try {
      const [{ data: profileRows }, { data: company }] = await Promise.all([
        api.get("/paddy-quality-lab-profiles"),
        api.get("/lab-validation-limits"),
      ]);
      setProfiles((profileRows || []).filter((p) => String(p.quality).toUpperCase() !== COMPANY_QUALITY));
      setCompanyLimits(mergeLimits(company?.parameter_limits));
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setLoading(false);
    }
  }, [canManage]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!quality && qualities.length) {
      setQuality(qualities[0].value);
    }
  }, [qualities, quality]);

  const selectedProfile = useMemo(
    () => profiles.find((p) => String(p.quality).toUpperCase() === String(quality).toUpperCase()),
    [profiles, quality],
  );

  const profileLimitsKey = selectedProfile
    ? `${selectedProfile.quality}:${JSON.stringify(selectedProfile.parameter_limits || {})}`
    : `new:${quality}`;

  useEffect(() => {
    if (!quality) return;
    const profile = profiles.find((p) => String(p.quality).toUpperCase() === String(quality).toUpperCase());
    const q = qualities.find((x) => x.value === quality);
    setLabel(profile?.label || q?.label || quality);
    setLimits(mergeLimits(profile?.parameter_limits));
  }, [quality, profileLimitsKey, lookupMap, profiles, qualities]);

  const setLimit = (key, field, value) => {
    setLimits((prev) => ({
      ...prev,
      [key]: { ...prev[key], [field]: value },
    }));
  };

  const setCompanyLimit = (key, field, value) => {
    setCompanyLimits((prev) => ({
      ...prev,
      [key]: { ...prev[key], [field]: value },
    }));
  };

  const saveCompany = async () => {
    try {
      setSavingCompany(true);
      await api.put("/lab-validation-limits", {
        label: "Company lab validation (all reports)",
        parameter_limits: payloadLimits(companyLimits),
        is_active: true,
      });
      toast.success("Company lab validation limits saved");
      await load();
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setSavingCompany(false);
    }
  };

  const rescan = async () => {
    try {
      setRescanning(true);
      const { data } = await api.post("/lab-validation-limits/rescan");
      const cleared = data.cleared_total ?? 0;
      const still = data.still_pending_total
        ?? ((data.truck_reports_flagged || data.truck_reports_updated || 0)
          + (data.composite_reports_flagged || data.composite_reports_updated || 0));
      toast.success(
        `Rescan complete: cleared ${cleared} false pending · ${still} still outside company limits`
        + ` (truck ${data.truck_reports_flagged ?? data.truck_reports_updated ?? 0}`
        + `, composite ${data.composite_reports_flagged ?? data.composite_reports_updated ?? 0})`,
      );
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setRescanning(false);
    }
  };

  const save = async () => {
    if (!quality) {
      toast.error("Select a paddy quality");
      return;
    }
    try {
      setSaving(true);
      const body = {
        label: label.trim() || quality,
        parameter_limits: payloadLimits(limits),
        is_active: true,
      };
      if (selectedProfile) {
        await api.patch(`/paddy-quality-lab-profiles/${encodeURIComponent(quality)}`, body);
      } else {
        await api.post("/paddy-quality-lab-profiles", { quality, ...body });
      }
      toast.success("Lab limits saved");
      await load();
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setSaving(false);
    }
  };

  if (!canManage) return <Navigate to="/" replace />;

  return (
    <div className="space-y-6" data-testid="paddy-quality-lab-profiles-page">
      <PageHeader
        eyebrow="Administration"
        title="Paddy quality lab limits"
        description="Company validation ranges apply to every lab report. Per-quality profiles can add tighter grading limits. Out-of-range reports are marked pending with a correction remark."
      />

      <SectionCard
        title="Company lab validation (all reports)"
        subtitle="Absolute min/max allowed values. 2 decimal places. Used for field validation on truck, composite, and stack sample labs."
        action={(
          <div className="flex flex-wrap gap-2">
            <Button type="button" variant="outline" onClick={rescan} disabled={rescanning || loading}>
              {rescanning ? "Scanning…" : "Rescan previous reports"}
            </Button>
            <Button
              type="button"
              className="bg-bansal-gold hover:bg-bansal-goldDark text-white"
              onClick={saveCompany}
              disabled={savingCompany || loading}
              data-testid="save-company-lab-limits"
            >
              {savingCompany ? "Saving…" : "Save company limits"}
            </Button>
          </div>
        )}
      >
        {loading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : (
          <LimitsTable limits={companyLimits} setLimit={setCompanyLimit} testIdPrefix="company-limit" />
        )}
      </SectionCard>

      <SectionCard title="Quality profile (optional tighter grading)">
        {loading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : qualities.length === 0 ? (
          <p className="text-sm text-amber-800">
            Add paddy varieties under Dropdown choices first, then return here to configure per-quality lab limits.
          </p>
        ) : (
          <div className="space-y-4 max-w-4xl">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Paddy quality</Label>
                <Select value={quality || "_none"} onValueChange={(v) => setQuality(v === "_none" ? "" : v)}>
                  <SelectTrigger className="mt-1.5" data-testid="quality-select">
                    <SelectValue placeholder="Select quality…" />
                  </SelectTrigger>
                  <SelectContent>
                    {qualities.map((q) => (
                      <SelectItem key={q.value} value={q.value}>
                        {q.label || q.value}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Display label</Label>
                <Input className="mt-1.5" value={label} onChange={(e) => setLabel(e.target.value)} />
              </div>
            </div>

            <LimitsTable limits={limits} setLimit={setLimit} testIdPrefix="limit" />

            <div className="flex justify-end">
              <Button
                type="button"
                className="bg-bansal-gold hover:bg-bansal-goldDark text-white"
                onClick={save}
                disabled={saving || !quality}
                data-testid="save-quality-lab-profile"
              >
                {saving ? "Saving…" : selectedProfile ? "Update quality limits" : "Create quality profile"}
              </Button>
            </div>
          </div>
        )}
      </SectionCard>
    </div>
  );
}
